import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { insightAPI } from '../../services/api';

export const fetchInsights = createAsyncThunk(
  'insights/fetchInsights',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await insightAPI.getAll(params);
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch insights');
    }
  }
);

export const fetchLatestInsights = createAsyncThunk(
  'insights/fetchLatestInsights',
  async (_, { rejectWithValue }) => {
    try {
      const response = await insightAPI.getAll({ type: 'latest', is_active: true });
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch latest insights');
    }
  }
);

export const fetchSingleInsight = createAsyncThunk(
  'insights/fetchSingleInsight',
  async (id, { rejectWithValue }) => {
    try {
      const response = await insightAPI.getById(id);
      return response.data.data || null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch insight');
    }
  }
);

export const fetchRelatedInsights = createAsyncThunk(
  'insights/fetchRelatedInsights',
  async ({ category, excludeId, limit = 3 }, { rejectWithValue }) => {
    try {
      const params = {
        is_active: true,
        limit,
      };
      if (category) {
        params.category = category;
      }
      const response = await insightAPI.getAll(params);
      const allInsights = response.data.data || [];
      const related = allInsights
        .filter(insight => insight.id !== excludeId)
        .slice(0, limit);
      return related;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch related insights');
    }
  }
);

const insightSlice = createSlice({
  name: 'insights',
  initialState: {
    items: [],
    latest: [],
    currentInsight: null,
    relatedInsights: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearInsights: (state) => {
      state.items = [];
      state.latest = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all insights
      .addCase(fetchInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch latest insights
      .addCase(fetchLatestInsights.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLatestInsights.fulfilled, (state, action) => {
        state.loading = false;
        state.latest = action.payload;
      })
      .addCase(fetchLatestInsights.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch single insight
      .addCase(fetchSingleInsight.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSingleInsight.fulfilled, (state, action) => {
        state.loading = false;
        state.currentInsight = action.payload;
      })
      .addCase(fetchSingleInsight.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentInsight = null;
      })
      // Fetch related insights
      .addCase(fetchRelatedInsights.pending, (state) => {
        // Don't set loading to true to avoid blocking UI
      })
      .addCase(fetchRelatedInsights.fulfilled, (state, action) => {
        state.relatedInsights = action.payload;
      })
      .addCase(fetchRelatedInsights.rejected, (state, action) => {
        state.relatedInsights = [];
      });
  },
});

export const { clearInsights } = insightSlice.actions;
export default insightSlice.reducer;

